<script setup>
import { useCartStore } from '~/stores/cartStore'
import { computed, ref } from 'vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const cartStore = useCartStore()

// --- Variations Logic ---
const selectedVariationId = ref(
  props.product.has_variations && props.product.variations?.length > 0
    ? props.product.variations[0].id 
    : null
)

const selectedVariation = computed(() => {
  if (!props.product.has_variations) return null;
  return props.product.variations?.find(v => v.id === selectedVariationId.value) || null;
})

const displayPrice = computed(() => {
  if (props.product.has_variations && selectedVariation.value) {
    return selectedVariation.value.offer_price || selectedVariation.value.price;
  }
  return props.product.offer_price || props.product.price;
})

// --- Gallery Logic ---
const isGalleryOpen = ref(false)
const activeImageIndex = ref(0)

const allImages = computed(() => {
  const images = []
  if (props.product.main) {
    images.push(props.product.main)
  }
  if (props.product.gallery && props.product.gallery.length > 0) {
    images.push(...props.product.gallery)
  }
  if (images.length === 0) {
    images.push({ original_url: 'https://via.placeholder.com/400x225' })
  }
  return images
})

const openGallery = () => {
  activeImageIndex.value = 0
  isGalleryOpen.value = true
}

const closeGallery = () => {
  isGalleryOpen.value = false
}

const nextImage = () => {
  if (activeImageIndex.value < allImages.value.length - 1) {
    activeImageIndex.value++
  } else {
    activeImageIndex.value = 0
  }
}

const prevImage = () => {
  if (activeImageIndex.value > 0) {
    activeImageIndex.value--
  } else {
    activeImageIndex.value = allImages.value.length - 1
  }
}

// --- Details Modal Logic ---
const isDetailsModalOpen = ref(false)
const modalQuantity = ref(1)

const openDetailsModal = () => {
  modalQuantity.value = 1
  isDetailsModalOpen.value = true
}
const closeDetailsModal = () => {
  isDetailsModalOpen.value = false
}

const incrementModalQuantity = () => {
  modalQuantity.value++
}

const decrementModalQuantity = () => {
  if (modalQuantity.value > 1) {
    modalQuantity.value--
  }
}

const handleAddQuantityToCart = () => {
  cartStore.addToCart(props.product, selectedVariation.value, modalQuantity.value)
  closeDetailsModal()
}
</script>

<template>
  <div class="card bg-base-100 shadow-sm border border-base-200 hover:shadow-md transition-all duration-300 w-full flex flex-col overflow-hidden group">
    
    <figure 
      class="relative w-full aspect-square overflow-hidden bg-base-200 cursor-pointer"
      @click="openGallery"
    >
      <img
        :src="allImages[0].original_url"
        :alt="product.name" 
        class="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" 
      >
      
      <div v-if="product.is_popular" class="absolute top-2 right-2 badge badge-sm bg-primary font-bold text-white shadow-md border-none z-10">
        Popular
      </div>
    </figure>

    <div class="p-3 flex flex-col grow gap-1 cursor-pointer" @click="openDetailsModal">
      <h2 class="text-sm font-bold leading-tight line-clamp-2 m-0 text-base-content group-hover:text-primary transition-colors">
        {{ product.name }}
      </h2>
      <div class="font-bold text-sm text-primary">
        Tk {{ displayPrice }}
      </div>
    </div>

    <!-- Gallery Modal -->
   <dialog class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': isGalleryOpen }">
      <div class="modal-box p-0 bg-neutral-900 w-full max-w-5xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col h-full sm:h-[85vh] max-h-screen">
        
        <div class="flex items-center justify-between p-4 absolute top-0 w-full z-20 bg-linear-to-b from-black/70 to-transparent pointer-events-none">
          <span class="text-white/90 text-sm font-medium tracking-wider drop-shadow-md">
            {{ activeImageIndex + 1 }} / {{ allImages.length }}
          </span>
          <button 
            class="text-white/70 hover:text-white transition-colors p-2 pointer-events-auto" 
            aria-label="Close Gallery"
            @click="closeGallery"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-7 h-7 drop-shadow-md">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="relative grow bg-black flex items-center justify-center overflow-hidden group">
          <img 
            :src="allImages[activeImageIndex].original_url" 
            class="max-h-full max-w-full object-contain transition-opacity duration-300" 
            alt="Gallery view"
          >

          <template v-if="allImages.length > 1">
            <div 
              class="absolute inset-y-0 left-0 w-1/3 flex items-center pl-2 sm:pl-6 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              @click="prevImage"
            >
              <div class="bg-black/20 hover:bg-black/60 p-2 sm:p-3 rounded-full text-white backdrop-blur-md transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-6 h-6 sm:w-8 sm:h-8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </div>
            </div>

            <div 
              class="absolute inset-y-0 right-0 w-1/3 flex items-center justify-end pr-2 sm:pr-6 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              @click="nextImage"
            >
              <div class="bg-black/20 hover:bg-black/60 p-2 sm:p-3 rounded-full text-white backdrop-blur-md transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-6 h-6 sm:w-8 sm:h-8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            </div>
          </template>
        </div>

        <div 
          v-if="allImages.length > 1" 
          class="p-4 flex gap-2 overflow-x-auto snap-x snap-mandatory bg-neutral-900 touch-pan-x [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] justify-start sm:justify-center"
        >
          <button 
            v-for="(img, idx) in allImages" 
            :key="idx"
            class="relative shrink-0 w-20 sm:w-24 aspect-video rounded-md overflow-hidden transition-all duration-200 focus:outline-none snap-center"
            :class="idx === activeImageIndex ? 'ring-2 ring-primary ring-offset-2 ring-offset-neutral-900 opacity-100' : 'opacity-40 hover:opacity-100'"
            @click="activeImageIndex = idx"
          >
            <img :src="img.original_url" class="object-cover w-full h-full" alt="Thumbnail" >
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop bg-black/90 backdrop-blur-sm" @click="closeGallery">
        <button>close</button>
      </form>
    </dialog>

    <!-- Details Modal -->
    <dialog class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': isDetailsModalOpen }">
      <div class="modal-box p-0 bg-base-100 w-full max-w-lg sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div class="flex items-center justify-between p-4 border-b border-base-200 sticky top-0 bg-base-100 z-10">
          <h3 class="font-bold text-lg line-clamp-1">{{ product.name }}</h3>
          <button 
            class="btn btn-circle btn-sm btn-ghost"
            @click="closeDetailsModal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        
        <div class="p-5 flex flex-col gap-4 overflow-y-auto">
          <div>
            <p class="text-sm text-base-content/70">{{ product.details }}</p>
          </div>

          <div class="flex items-center justify-between">
            <span class="font-bold text-2xl text-primary font-mono">Tk {{ displayPrice }}</span>
          </div>

          <div v-if="product.has_variations" class="form-control w-full">
            <label class="label">
              <span class="label-text font-bold">Choose Variation</span>
            </label>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="variation in product.variations" 
                :key="variation.id"
                class="btn btn-sm h-auto py-2 text-left"
                :class="selectedVariationId === variation.id ? 'btn-primary' : 'btn-outline'"
                @click="selectedVariationId = variation.id"
              >
                {{ variation.name }} <br>
                <span class="text-xs opacity-80">Tk {{ variation.offer_price || variation.price }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="p-4 border-t border-base-200 mt-auto bg-base-100 sticky bottom-0 flex flex-col gap-3">
            <div 
              class="flex items-center justify-between h-12 rounded-lg overflow-hidden shadow-inner w-full bg-base-200"
            >
              <button 
                class="h-full px-6 text-red-500 hover:bg-base-300 transition-colors flex items-center justify-center" 
                @click="decrementModalQuantity" 
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14" />
                </svg>
              </button>

              <span class="font-bold text-xl select-none">
                {{ modalQuantity }}
              </span>

              <button 
                class="h-full px-6 text-primary hover:bg-base-300 transition-colors flex items-center justify-center"
                @click="incrementModalQuantity"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </div>

             <button
              class="btn btn-primary w-full shadow-lg" 
              @click="handleAddQuantityToCart"
            > 
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                <path d="M1 1.75A.75.75 0 0 1 1.75 1h1.628a1.75 1.75 0 0 1 1.734 1.51L5.18 3a65.25 65.25 0 0 1 13.36 1.412.75.75 0 0 1 .58.875 48.645 48.645 0 0 1-1.618 6.2.75.75 0 0 1-.712.513H6a2.503 2.503 0 0 0-2.292 1.5H17.25a.75.75 0 0 1 0 1.5H2.76a.75.75 0 0 1-.748-.807 4.002 4.002 0 0 1 2.716-3.486L3.626 2.716a.25.25 0 0 0-.248-.216H1.75A.75.75 0 0 1 1 1.75ZM6 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM15.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
              </svg>
              Add to Cart
            </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop bg-black/60 backdrop-blur-sm" @click="closeDetailsModal">
        <button>close</button>
      </form>
    </dialog>

  </div>
</template>